import { useCallback, useEffect, useRef, useState } from 'react';
import type { WorkflowJob } from '../../graphql/types';
import {
  getTitleAnalysisResults,
  runTitleAnalysis,
  type TitleAnalysisResultsResponse,
} from '../../services/guidelinesApiService';
import { ALLOWED_USE_CASES, isRunControlDisabled, type UseCase } from '../../titleAnalysis';
import {
  MAX_CONSECUTIVE_POLL_ERRORS,
  TITLE_ANALYSIS_POLL_INTERVAL_MS,
  phaseFromResults,
  type TitleAnalysisPhase,
} from '../../components/titleAnalysis/TitleAnalysisSection';

/**
 * Title analysis run state for the Workspace Title analysis Mode.
 *
 * Same phase machine as `TitleAnalysisSection` (loading | idle | starting |
 * running | stale | completed | failed | loadError): `phaseFromResults` and the
 * stale detection, 5 second polling while running, and the stored report kept
 * on screen during a re-run or after a failed/stale run.
 *
 * Requirements: 11.1, 11.4, 11.5.
 */

export const START_ERROR_MESSAGE =
  'The analysis could not be started. Check your connection and try again.';
export const ALREADY_RUNNING_NOTICE = 'An analysis is already running for this search.';

/** The job's stored use case when it is one of the five options, else null. */
export function initialUseCase(job: Pick<WorkflowJob, 'useCase'>): UseCase | null {
  const stored = job.useCase;
  return stored && (ALLOWED_USE_CASES as readonly string[]).includes(stored)
    ? (stored as UseCase)
    : null;
}

export interface TitleAnalysisRun {
  phase: TitleAnalysisPhase;
  results: TitleAnalysisResultsResponse | null;
  selectedUseCase: UseCase | null;
  setSelectedUseCase: (value: UseCase) => void;
  startError: string | null;
  notice: string | null;
  run: () => Promise<void>;
  reload: () => Promise<void>;
}

export function useTitleAnalysisRun(job: Pick<WorkflowJob, 'jobId' | 'useCase'>): TitleAnalysisRun {
  const { jobId } = job;
  const [phase, setPhaseState] = useState<TitleAnalysisPhase>('loading');
  const [selectedUseCase, setSelectedUseCase] = useState<UseCase | null>(() => initialUseCase(job));
  const [results, setResults] = useState<TitleAnalysisResultsResponse | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const phaseRef = useRef<TitleAnalysisPhase>(phase);
  const setPhase = useCallback((next: TitleAnalysisPhase) => {
    phaseRef.current = next;
    setPhaseState(next);
  }, []);

  const mountedRef = useRef(true);
  const loadSeqRef = useRef(0);
  const pollInFlightRef = useRef(false);
  const pollErrorsRef = useRef(0);

  const applyResults = useCallback(
    (loaded: TitleAnalysisResultsResponse) => {
      pollErrorsRef.current = 0;
      setResults(loaded);
      setPhase(phaseFromResults(loaded, Date.now()));
    },
    [setPhase]
  );

  const reload = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setPhase('loading');
    try {
      const loaded = await getTitleAnalysisResults(jobId);
      if (!mountedRef.current || seq !== loadSeqRef.current) return;
      applyResults(loaded);
    } catch (err) {
      console.error('[TitleAnalysisMode] Failed to load results:', err);
      if (!mountedRef.current || seq !== loadSeqRef.current) return;
      setPhase('loadError');
    }
  }, [jobId, applyResults, setPhase]);

  // Initial load of any stored report.
  useEffect(() => {
    mountedRef.current = true;
    void reload();
    return () => {
      mountedRef.current = false;
    };
    // Mount-only: later status changes are driven by this hook's own polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll the results route every 5 seconds, only while a run is active.
  useEffect(() => {
    if (phase !== 'running') return undefined;

    const tick = async () => {
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      try {
        const loaded = await getTitleAnalysisResults(jobId);
        if (!mountedRef.current || phaseRef.current !== 'running') return;
        applyResults(loaded);
      } catch (err) {
        console.error('[TitleAnalysisMode] Poll failed:', err);
        if (!mountedRef.current || phaseRef.current !== 'running') return;
        pollErrorsRef.current += 1;
        if (pollErrorsRef.current >= MAX_CONSECUTIVE_POLL_ERRORS) setPhase('loadError');
      } finally {
        pollInFlightRef.current = false;
      }
    };

    const id = setInterval(() => void tick(), TITLE_ANALYSIS_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [phase, jobId, applyResults, setPhase]);

  const run = useCallback(async () => {
    const current = phaseRef.current;
    const busy = current === 'loading' || current === 'starting' || current === 'running';
    if (isRunControlDisabled(selectedUseCase, busy)) return;
    setStartError(null);
    setNotice(null);
    setPhase('starting');
    try {
      const dispatched = await runTitleAnalysis({
        job_id: jobId,
        use_case: selectedUseCase ?? undefined,
      });
      if (!mountedRef.current) return;
      if (dispatched.already_running) setNotice(ALREADY_RUNNING_NOTICE);
      pollErrorsRef.current = 0;
      setPhase('running');
    } catch (err) {
      console.error('[TitleAnalysisMode] Failed to start analysis:', err);
      if (!mountedRef.current) return;
      setStartError(START_ERROR_MESSAGE);
      // Re-enable the run control and keep any displayed report unchanged.
      setPhase(current === 'starting' ? 'idle' : current);
    }
  }, [jobId, selectedUseCase, setPhase]);

  return {
    phase,
    results,
    selectedUseCase,
    setSelectedUseCase,
    startError,
    notice,
    run,
    reload,
  };
}
